import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { team } from "@duress/shared";
import { listTeamRoster, setTeamRosterExcluded } from "./team-roster-api";

function teamRosterKey(clientId: string) {
  return ["team-roster", clientId] as const;
}

export function useTeamRoster(clientId: string): {
  data: team.TeamRosterListResponse | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: teamRosterKey(clientId),
    queryFn: () => listTeamRoster(clientId),
    enabled: Boolean(clientId),
  });
  return { data, isLoading, error, refetch: () => void refetch() };
}

interface SetExcludedArgs {
  teamId: string;
  excluded: boolean;
}

// Excluding/including a team recomputes every stored period on the backend,
// so the period detail and range/all-time views the rest of the app reads
// are stale until refetched too.
export function useSetTeamRosterExcluded(clientId: string): {
  mutateAsync: (args: SetExcludedArgs) => Promise<team.TeamRosterEntry>;
  isPending: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (args: SetExcludedArgs) =>
      setTeamRosterExcluded(clientId, args.teamId, args.excluded),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamRosterKey(clientId) });
      void qc.invalidateQueries({ queryKey: ["periods", clientId] });
      void qc.invalidateQueries({ queryKey: ["sessions-compute", clientId] });
      void qc.invalidateQueries({ queryKey: ["clients"] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, error: m.error };
}
